import { useState, useEffect } from "react";
import { ErrorBoundary, Suspense } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ArticleFiltersComponent } from "@/components/articles/article-filters";
import { ArticleTable } from "@/components/articles/article-table";
import { NewArticleDialog } from "@/components/articles/new-article-dialog";
import { articleListQueryOptions } from "@/hooks/use-articles";
import { usePagination } from "@/hooks/use-pagination";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import type { AdminArticleFilters } from "@/types/article";

export default function ArticlesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [articleFilters, setArticleFiltersState] =
    useState<AdminArticleFilters>({});
  const articlePagination = usePagination({ initialLimit: 10 });
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery(
      articleListQueryOptions(articleFilters, {
        page: articlePagination.page + 1,
        limit: articlePagination.limit,
      })
    );
  }, [queryClient, articleFilters, articlePagination.page, articlePagination.limit]);

  const setArticleFilters = (newFilters: AdminArticleFilters) => {
    setArticleFiltersState(newFilters);
    articlePagination.setPage(1);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            아티클 관리
          </h1>
          <p className="text-muted-foreground leading-normal">
            아티클 목록을 관리하고 새 아티클을 작성합니다.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          새 아티클
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>아티클 목록</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-4 md:px-6">
          <ArticleFiltersComponent
            filters={articleFilters}
            onFiltersChange={setArticleFilters}
          />

          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<TableLoadingFallback columnCount={6} />}>
                  <SuspenseQuery
                    {...articleListQueryOptions(articleFilters, {
                      page: articlePagination.page,
                      limit: articlePagination.limit,
                    })}
                  >
                    {({ data }) => (
                      <ArticleTable
                        data={data.items}
                        meta={data.meta}
                        pagination={articlePagination}
                      />
                    )}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>

      <NewArticleDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
